import { cn } from "cn";
import {
  AlertCircle,
  Check,
  ClipboardList,
  Loader2,
  TriangleAlert,
} from "lucide-react";
import type * as React from "react";

export type StatusGlyphKind =
  | "done"
  | "review"
  | "writing"
  | "error"
  | "draft"
  | "none";

// One glyph per visit, as on iPad/iPhone and the v3.2 sidebar (.vglyph): a
// green check when ready, half amber while values still need review, a
// turning gradient while the note is being written, a red ring on error, a
// dashed ring for a pre-chart draft. Same look as apps/web's VisitStatusIcon.
export function StatusGlyph({
  glyph,
  label,
  className,
}: {
  glyph: StatusGlyphKind;
  /** What the glyph means, for screen readers ("Ready", "Needs review"...). */
  label: string;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      aria-label={label}
      className={cn(
        "relative inline-flex size-4 shrink-0 items-center justify-center rounded-full",
        className,
        glyph === "done" && "bg-v3-ok text-white",
        glyph === "review" &&
          "bg-[linear-gradient(90deg,var(--v3-warn)_50%,transparent_50%)] shadow-[inset_0_0_0_1.5px_var(--v3-warn)]",
        glyph === "writing" &&
          "animate-spin bg-[conic-gradient(#00c2ff,#3d7bff,#8f7cff,#00c2ff)] [animation-duration:2s] motion-reduce:animate-none",
        glyph === "error" &&
          "shadow-[inset_0_0_0_1.5px_var(--v3-rec)] after:size-2 after:rounded-full after:bg-v3-rec",
        glyph === "draft" && "border-[1.5px] border-dashed border-v3-text-2",
        glyph === "none" && "shadow-[inset_0_0_0_1.5px_var(--v3-text-3)]",
      )}
      data-glyph={glyph}
      role="img"
    >
      {glyph === "done" && (
        <Check aria-hidden className="size-2.5" strokeWidth={3.5} />
      )}
    </span>
  );
}

export type StatusBadgeKind =
  | "processing"
  | "writing"
  | "ready"
  | "review"
  | "error"
  | "draft";

const PILL =
  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 font-v3 text-xs font-semibold leading-5";

const BADGE: Record<StatusBadgeKind, string> = {
  processing: "bg-v3-brand-tint text-v3-brand-strong",
  writing: "bg-v3-brand-tint text-v3-brand-strong",
  ready: "px-0 font-medium text-v3-text-3",
  review: "bg-v3-warn-tint text-[12px] text-v3-warn-text",
  error: "bg-v3-rec-tint text-v3-rec-text",
  draft:
    "border border-dashed border-v3-border-strong bg-v3-panel py-px text-v3-text-2",
};

const ICON: Record<StatusBadgeKind, React.ReactNode> = {
  processing: <Loader2 aria-hidden className="size-3.5 animate-spin" />,
  writing: <Loader2 aria-hidden className="size-3.5 animate-spin" />,
  ready: <span aria-hidden className="size-2 rounded-full bg-v3-ok" />,
  review: <TriangleAlert aria-hidden className="size-3.5" />,
  error: <AlertCircle aria-hidden className="size-3.5" />,
  draft: <ClipboardList aria-hidden className="size-3.5" />,
};

// The status pill of a visit row (apps/web VisitStatusBadge and the "needs
// review" pill): work in progress and errors stand out, Ready stays quiet
// (a dot), a pre-chart draft has a dashed outline.
export function StatusBadge({
  status,
  children,
  className,
  ...props
}: React.ComponentPropsWithRef<"span"> & {
  status: StatusBadgeKind;
  /** The label ("Writing note", "Ready", "2 to review"...). */
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={cn(PILL, BADGE[status], className)}
      data-status={status}
      {...props}
    >
      {ICON[status]}
      {children}
    </span>
  );
}
